import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import EntityForm from '../components/EntityForm/EntityForm';
import formStyles from '../components/EntityForm/EntityForm.module.css';
import HistoryAccordion from '../components/HistoryAccordion/HistoryAccordion';
import { getGolfCourse, createGolfCourse, updateGolfCourse, archiveGolfCourse, isAdmin as checkAdmin } from '../api';
import useEntityForm from '../hooks/useEntityForm';

export default function GolfCourseFormPage({ user }) {
  const { id } = useParams();
  const isAdmin = checkAdmin(user);

  const [name, setName] = useState('');
  const [holes, setHoles] = useState('');
  const [allowsCarts, setAllowsCarts] = useState(true);
  const [membersOnly, setMembersOnly] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const {
    isEdit,
    isBusy,
    formError,
    setFormError,
    setIsLoading,
    isArchived,
    setIsArchived,
    pageTitle,
    setPageTitle,
    handleCancel,
    handleArchive,
    handleRestore,
    runSubmit,
  } = useEntityForm({
    id,
    backPath: '/golf-courses',
    archiveFn: archiveGolfCourse,
    unarchiveFn: (entityId) => updateGolfCourse(entityId, { is_archived: false }),
    newLabel: 'New golf course',
  });

  useEffect(() => {
    if (!isEdit) return;
    setIsLoading(true);
    getGolfCourse(id)
      .then((data) => {
        const course = data.golf_course;
        setName(course.name || '');
        setHoles(String(course.holes || ''));
        setAllowsCarts(course.allows_carts !== false);
        setMembersOnly(course.members_only === true);
        setIsArchived(course.is_archived || false);
        setPageTitle(course.name || 'Golf course');
      })
      .catch((err) => {
        setFormError(err.message || 'Failed to load golf course.');
        setPageTitle('Golf course');
      })
      .finally(() => setIsLoading(false));
  }, [id, isEdit]);

  const validate = useCallback(() => {
    const errors = {};
    if (!name.trim()) errors.name = 'Course name is required.';
    if (!holes) errors.holes = 'Please select the number of holes.';
    return errors;
  }, [name, holes]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError('');
      const errors = validate();
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      setFieldErrors({});
      const payload = { name: name.trim(), holes: Number(holes), allows_carts: allowsCarts, members_only: membersOnly };
      await runSubmit(() => isEdit ? updateGolfCourse(id, payload) : createGolfCourse(payload));
    },
    [id, isEdit, name, holes, allowsCarts, membersOnly, validate, runSubmit, setFormError]
  );

  return (
    <EntityForm
      title={pageTitle}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      submitLabel={isEdit ? 'Save changes' : 'Create golf course'}
      isSubmitting={isBusy}
      error={formError}
      canDelete={isEdit && isAdmin && !isArchived}
      onDelete={handleArchive}
      confirmMessage={`Archive "${name}"? This will remove it from active use.`}
      canRestore={isEdit && isAdmin && isArchived}
      onRestore={() => handleRestore(`Restore "${name}"? It will become active again.`)}
      restoreLabel="Restore golf course"
      belowCard={isEdit ? <HistoryAccordion entityPath={`/golf-courses/${id}`} /> : null}
    >
      {/* Course name — full width */}
      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="courseName">
          Course name <span className={formStyles.required} aria-hidden="true">*</span>
        </label>
        <input
          id="courseName"
          type="text"
          className={`${formStyles.input} ${fieldErrors.name ? formStyles.inputError : ''}`}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (fieldErrors.name) setFieldErrors((prev) => ({ ...prev, name: '' }));
          }}
          placeholder="e.g. North Links Course"
          disabled={isBusy}
          autoFocus={!isEdit}
          required
        />
        {fieldErrors.name && (
          <span className={formStyles.fieldError} role="alert">{fieldErrors.name}</span>
        )}
      </div>

      {/* Holes + Allows carts — half width each */}
      <div className={formStyles.fieldRow}>
        <div className={formStyles.fieldHalf}>
          <label className={formStyles.label} htmlFor="courseHoles">
            Holes <span className={formStyles.required} aria-hidden="true">*</span>
          </label>
          <select
            id="courseHoles"
            className={`${formStyles.select} ${fieldErrors.holes ? formStyles.inputError : ''}`}
            value={holes}
            onChange={(e) => {
              setHoles(e.target.value);
              if (fieldErrors.holes) setFieldErrors((prev) => ({ ...prev, holes: '' }));
            }}
            disabled={isBusy}
            required
          >
            <option value="">Select holes…</option>
            <option value="9">9 holes</option>
            <option value="18">18 holes</option>
          </select>
          {fieldErrors.holes && (
            <span className={formStyles.fieldError} role="alert">{fieldErrors.holes}</span>
          )}
        </div>
        <div className={formStyles.fieldHalf}>
          <span className={formStyles.label}>Allows carts</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, cursor: isBusy ? 'not-allowed' : 'pointer' }}>
            <input
              id="courseAllowsCarts"
              type="checkbox"
              checked={allowsCarts}
              onChange={(e) => setAllowsCarts(e.target.checked)}
              disabled={isBusy}
              style={{ margin: 0, cursor: 'inherit', accentColor: 'var(--cp-azure-600)', width: 16, height: 16 }}
            />
            <span style={{ fontSize: 14, color: 'var(--cp-gray-700)' }}>
              {allowsCarts ? 'Yes' : 'No'}
            </span>
          </label>
        </div>
      </div>

      {/* Members only — half width */}
      <div className={formStyles.fieldRow}>
        <div className={formStyles.fieldHalf}>
          <span className={formStyles.label}>Members only</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, cursor: isBusy ? 'not-allowed' : 'pointer' }}>
            <input
              id="courseMembersOnly"
              type="checkbox"
              checked={membersOnly}
              onChange={(e) => setMembersOnly(e.target.checked)}
              disabled={isBusy}
              style={{ margin: 0, cursor: 'inherit', accentColor: 'var(--cp-azure-600)', width: 16, height: 16 }}
            />
            <span style={{ fontSize: 14, color: 'var(--cp-gray-700)' }}>
              {membersOnly ? 'Yes' : 'No'}
            </span>
          </label>
        </div>
      </div>
    </EntityForm>
  );
}
